import { useState } from "react";
import { Link } from "react-router-dom";
import { Plus, ArrowRight } from "lucide-react";

import apiClient from "@/api/client";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { PageLoader } from "@/components/ui/Spinner";
import { formatDate } from "@/utils/formatDate";

import { CreateAgreementDialog } from "../components/CreateAgreementDialog";
import { useEmployeeAgreements } from "../hooks/useEmployeeAgreements";

interface AllUsersRow {
  id: string;
  username: string;
  first_name: string;
  last_name: string;
  role: string;
  is_signatory?: boolean;
}

function useInternalUsers() {
  return useQuery({
    queryKey: ["all-users-internal"],
    queryFn: () =>
      apiClient
        .get<{ data: AllUsersRow[] }>("/users/")
        .then((r) => r.data.data.filter((u) => u.role !== "contractor")),
  });
}

export function EmployeeAgreementsListPage() {
  const [createOpen, setCreateOpen] = useState(false);
  const { data: agreements = [], isLoading } = useEmployeeAgreements();
  const { data: internalUsers = [] } = useInternalUsers();

  if (isLoading) return <PageLoader />;

  const employees = internalUsers.map((u) => ({
    id: u.id,
    full_name:
      `${u.first_name ?? ""} ${u.last_name ?? ""}`.trim() || u.username,
  }));

  return (
    <div className="p-3 sm:p-6 max-w-6xl mx-auto flex flex-col gap-4 sm:gap-6">
      <div className="flex items-start sm:items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <h1 className="text-base sm:text-lg font-mono uppercase tracking-wider">
            Umowy Ramowe pracownik-firma
          </h1>
          <p className="text-xs sm:text-sm text-[var(--color-text-muted)] mt-1 hidden sm:block">
            Umowy nadające pracownikom wewnętrznym sprawdzania i prawo
            podpisywania dokumentacji projektowej.
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="flex-shrink-0">
          <Plus size={14} /> Nowa UR
        </Button>
      </div>

      {agreements.length === 0 ? (
        <section className="bg-white dark:bg-gray-800 border border-[var(--color-border)]">
          <div className="text-sm text-[var(--color-text-muted)] py-16 px-6 text-center">
            Brak umów. Utwórz pierwszą, żeby nadać pracownikowi uprawnienie
            podpisywania.
          </div>
        </section>
      ) : (
        <>
          {/* Mobile card list */}
          <div className="sm:hidden flex flex-col gap-2">
            {agreements.map((ag) => (
              <Link
                key={ag.id}
                to={`/ceo/employee-agreements/${ag.id}`}
                className="bg-white dark:bg-gray-800 border border-[var(--color-border)] active:bg-[var(--color-bg-hover)] transition-colors px-4 py-3 flex flex-col gap-2"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="font-semibold text-sm truncate">
                      {ag.employee_detail.full_name}
                    </p>
                    <p className="text-sm text-[var(--color-text-secondary)] mt-0.5 line-clamp-2">
                      {ag.title}
                    </p>
                  </div>
                  <ArrowRight
                    size={16}
                    className="flex-shrink-0 text-[var(--color-text-muted)] mt-0.5"
                  />
                </div>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    {ag.status === "active" ? (
                      <Badge color="green">Aktywna</Badge>
                    ) : (
                      <Badge color="gray">Rozwiązana</Badge>
                    )}
                    {ag.is_b2b && <Badge color="blue">B2B</Badge>}
                  </div>
                  <span className="text-xs font-mono text-[var(--color-text-muted)]">
                    {formatDate(ag.signed_date)}
                  </span>
                </div>
              </Link>
            ))}
          </div>

          {/* Desktop table */}
          <section className="hidden sm:block bg-white dark:bg-gray-800 border border-[var(--color-border)] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-bg-elevated)]">
                <tr className="text-xs uppercase tracking-wider text-[var(--color-text-muted)]">
                  <th className="text-left px-4 py-3 font-mono">Pracownik</th>
                  <th className="text-left px-4 py-3 font-mono">Tytuł</th>
                  <th className="text-left px-4 py-3 font-mono">
                    Data podpisania
                  </th>
                  <th className="text-left px-4 py-3 font-mono">Status</th>
                  <th className="text-left px-4 py-3 font-mono">B2B</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border)]">
                {agreements.map((ag) => (
                  <tr
                    key={ag.id}
                    className="hover:bg-[var(--color-bg-hover)] transition-colors"
                  >
                    <td className="px-4 py-3 font-medium">
                      {ag.employee_detail.full_name}
                    </td>
                    <td className="px-4 py-3">{ag.title}</td>
                    <td className="px-4 py-3 font-mono text-[var(--color-text-muted)]">
                      {formatDate(ag.signed_date)}
                    </td>
                    <td className="px-4 py-3">
                      {ag.status === "active" ? (
                        <Badge color="green">Aktywna</Badge>
                      ) : (
                        <Badge color="gray">Rozwiązana</Badge>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      {ag.is_b2b ? (
                        <Badge color="blue">Tak</Badge>
                      ) : (
                        <span className="text-[var(--color-text-muted)]">
                          Nie
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to={`/ceo/employee-agreements/${ag.id}`}
                        className="text-sm hover:underline inline-flex items-center gap-1 text-[var(--color-text-secondary)]"
                      >
                        Szczegóły <ArrowRight size={12} />
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}

      <CreateAgreementDialog
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        employees={employees}
      />
    </div>
  );
}
